import React from 'react';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import { useToast } from '../../providers/ToastProvider';
import { useUser } from '../../providers/UserProvider';
import { markChatAsSeen } from '../../services/patient';
import {
    type ChatMessage,
    type EditingMessage,
    ChatConfigProvider,
    ChatInput,
    SearchStrip,
    MessageBubble,
    PendingMessageBubble,
    FailedFilesWarning,
    OverlayScrollArea,
    useChatSubmit,
    useChatSurface,
    usePatientChat
} from '@odo/shared-ui';
import { useDoctorChatConfig } from './chatConfig';
import { MarkAsReadButton } from '../MarkAsReadButton';

// --- ChatPanel ---

interface ChatPanelProps {
    openRef?: React.MutableRefObject<(() => void) | null>;
    /** Layout/frame classes for the panel container — every surface supplies its own. */
    className: string;
    /**
     * Whether the slim "Mark as read" banner is shown. Off for surfaces
     * that already expose their own mark-as-read control (e.g. the sliding
     * conversation panel); on for the approval-page Message ODO tab.
     */
    showMarkAsSeenBanner?: boolean;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
    openRef,
    className,
    showMarkAsSeenBanner = true
}) => {
    const {
        patientId,
        isOpen,
        open,
        messages,
        loading,
        error,
        send,
        edit,
        fileUploadProgress,
        otherSideLastSeenAt,
        feed,
        uploadInlineImage
    } = usePatientChat();
    const { user } = useUser();
    const chatConfig = useDoctorChatConfig(otherSideLastSeenAt, feed?.fileUrls);
    const { hasUnreadMessages, refresh: refreshMetadata } = usePatientMetadata();
    const { showToast } = useToast();
    const showMarkAsSeen = hasUnreadMessages(patientId);

    React.useEffect(() => {
        if (openRef) openRef.current = open;
    }, [open, openRef]);

    React.useEffect(() => {
        if (!isOpen) open();
    }, [isOpen, open]);

    const surface = useChatSurface({
        messages,
        isActive: isOpen,
        scrollToBottomOnActivate: true,
        deferNewMessageScroll: false
    });

    const {
        containerRef,
        listRef,
        bottomRef,
        searchInputRef,
        sending,
        setSending,
        pendingMessage,
        beginPendingMessage,
        markPendingMessageFailed,
        dismissPendingMessage,
        pendingFiles,
        onFileChange,
        onAddFiles,
        onRemoveFile,
        failedFileNames,
        clearFailedFileNames,
        searchQuery,
        setSearchQuery,
        isSearchOpen,
        activeMatchIdx,
        perMessageMatches,
        flatMatchesCount,
        onPrevMatch,
        onNextMatch,
        closeSearch,
        toggleSearch
    } = surface;

    const [editingMessage, setEditingMessage] = React.useState<EditingMessage | null>(null);
    const [isMarkingRead, setIsMarkingRead] = React.useState(false);

    const onEditError = React.useCallback(
        (message: string) => showToast(message, 'error'),
        [showToast]
    );

    const onSubmit = useChatSubmit({
        surface,
        send,
        edit,
        editingMessage,
        setEditingMessage,
        onEditError
    });

    const onStartEdit = React.useCallback((message: ChatMessage) => {
        const isHtml = message.format === 'html' && !!message.htmlBody;
        setEditingMessage({
            id: message.id,
            html: isHtml ? (message.htmlBody as string) : message.text,
            doc: message.bodyDoc
        });
    }, []);

    const onCancelEdit = React.useCallback(() => {
        setEditingMessage(null);
    }, []);

    const onRetryFailedMessage = React.useCallback(async () => {
        if (!pendingMessage || !pendingMessage.failed || sending) return;
        try {
            setSending(true);
            beginPendingMessage(pendingMessage.text, pendingMessage.files, pendingMessage.doc);
            await send(pendingMessage.text, pendingMessage.files, pendingMessage.doc);
        } catch {
            markPendingMessageFailed();
        } finally {
            setSending(false);
        }
    }, [pendingMessage, sending, send, setSending, beginPendingMessage, markPendingMessageFailed]);

    const handleMarkAsSeenClick = async () => {
        setIsMarkingRead(true);
        try {
            await markChatAsSeen(patientId);
            await refreshMetadata();
        } catch {
            showToast('Failed to mark chat as seen', 'error');
        } finally {
            setIsMarkingRead(false);
        }
    };

    return (
        <ChatConfigProvider config={chatConfig}>
            <div ref={containerRef} className={className}>
                {/* Slim header with only Mark-as-seen, shown only when there are unread
            messages. The tab itself provides the title and "close" affordance,
            so we don't repeat them here. */}
                {showMarkAsSeenBanner && showMarkAsSeen && (
                    <div className="flex-shrink-0 flex items-center gap-2.5 px-4 py-2.5 border-b border-yellow-200 bg-yellow-50">
                        {/* Same dot the Message ODO tab uses, so the bar reads as
                            the same notification the tab is flagging. */}
                        <span
                            aria-hidden
                            className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                        />
                        <span className="flex-1 text-sm font-medium text-gray-800">
                            New messages from the ODO team
                        </span>
                        <MarkAsReadButton
                            onClick={handleMarkAsSeenClick}
                            isLoading={isMarkingRead}
                        />
                    </div>
                )}

                {/* Search */}
                {isSearchOpen && (
                    <SearchStrip
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        activeMatchIdx={activeMatchIdx}
                        flatMatchesCount={flatMatchesCount}
                        onPrevMatch={onPrevMatch}
                        onNextMatch={onNextMatch}
                        onClose={closeSearch}
                        inputRef={searchInputRef}
                    />
                )}

                {/* Messages */}
                <OverlayScrollArea
                    scrollRef={listRef}
                    className="flex-1 min-h-0 bg-white"
                    contentClassName="px-4 py-3"
                    innerClassName={
                        messages.length === 0 && !loading && !pendingMessage
                            ? 'min-h-full flex items-center justify-center'
                            : 'space-y-4'
                    }
                >
                    {messages.length === 0 && loading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin mr-2" />
                            <div className="text-xs text-gray-500">Loading&hellip;</div>
                        </div>
                    )}
                    {messages.length === 0 && !loading && !pendingMessage && (
                        <div className="text-[#737373] text-sm italic">
                            How can we help you today?
                        </div>
                    )}
                    {messages.map((m: ChatMessage, msgIndex: number) => (
                        <MessageBubble
                            key={m.id}
                            message={m}
                            msgIndex={msgIndex}
                            perMessageMatches={perMessageMatches}
                            activeMatchIdx={activeMatchIdx}
                            searchQuery={searchQuery}
                            currentUserEmail={user?.email}
                            onStartEdit={onStartEdit}
                        />
                    ))}

                    {pendingMessage && (
                        <PendingMessageBubble
                            pendingMessage={pendingMessage}
                            fileUploadProgress={fileUploadProgress}
                            sending={sending}
                            onRetry={onRetryFailedMessage}
                            onDismiss={dismissPendingMessage}
                            userName={user?.name}
                            userEmail={user?.email}
                        />
                    )}

                    {failedFileNames.length > 0 && (
                        <FailedFilesWarning
                            fileNames={failedFileNames}
                            onDismiss={clearFailedFileNames}
                        />
                    )}

                    <div ref={bottomRef} />
                    {loading && messages.length > 0 && (
                        <div className="flex items-center justify-center py-2">
                            <div className="h-3 w-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin mr-2" />
                            <div className="text-[10px] text-gray-500">Refreshing&hellip;</div>
                        </div>
                    )}
                    {error && <div className="text-xs text-red-500">{error}</div>}
                </OverlayScrollArea>

                {/* Input */}
                <ChatInput
                    onSubmit={onSubmit}
                    pendingFiles={pendingFiles}
                    onFileChange={onFileChange}
                    onAddFiles={onAddFiles}
                    onRemoveFile={onRemoveFile}
                    sending={sending}
                    isSearchOpen={isSearchOpen}
                    onToggleSearch={toggleSearch}
                    draftKey={patientId ? `chat-draft-${patientId}` : undefined}
                    editingMessage={editingMessage}
                    onCancelEdit={onCancelEdit}
                    onImageUpload={uploadInlineImage}
                />
            </div>
        </ChatConfigProvider>
    );
};
